import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { removeProps } from '@iqd/shared-quoting/core';
import { Map as ImmutableMap } from 'immutable';
import { SETTINGS_QUERY, type SettingsQueryResult } from '../graphql/settings';

type SettingsMap = ImmutableMap<string, unknown>;

export interface UseSettingsResult {
  settings: SettingsMap | null;
  loaded: boolean;
  loading: boolean;
  error: Error | null;
}

function toMap(rows: SettingsQueryResult['settings']): SettingsMap {
  const entries: Array<[string, unknown]> = rows.map((s) => {
    if (s.value === 'true' || s.value === 'false') {
      // Compared, not coerced: `Boolean('false')` is `true`, so a setting
      // stored as the string "false" used to read as enabled. The same line
      // was in iqd-pro and the distributor portal and is fixed in all three
      // together — one app disagreeing about which features are on is worse
      // than the bug.
      return [s.key, s.value === 'true'];
    }
    if (!isNaN(Number(s.value))) {
      return [s.key, Number(s.value)];
    }
    return [s.key, removeProps(s.value, '__typename')];
  });
  return ImmutableMap(entries);
}

export function useSettings(): UseSettingsResult {
  const { data, loading, error } = useQuery<SettingsQueryResult>(SETTINGS_QUERY);
  const settings = useMemo(() => (data?.settings ? toMap(data.settings) : null), [data]);
  return {
    settings,
    loaded: !!settings,
    loading,
    error: error ?? null,
  };
}
